/**
 * W3C Web Annotation selector capture.
 *
 * Produces a combined selector stored as JSON in annotations.selector:
 *   { position: { start, end }, quote: { exact, prefix, suffix } }
 *
 * position uses character offsets into the container's textContent.
 * quote carries 32-char context strings for fuzzy re-attach after edits.
 */

const CONTEXT_LEN = 32;

export interface Selector {
  position: { start: number; end: number };
  quote: { exact: string; prefix: string; suffix: string };
}

/** Walk text nodes inside `container` in DOM order, accumulating offsets. */
function walkTextNodes(container: HTMLElement): { node: Text; start: number; end: number }[] {
  const result: { node: Text; start: number; end: number }[] = [];
  let offset = 0;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const text = n as Text;
    const len = text.data.length;
    result.push({ node: text, start: offset, end: offset + len });
    offset += len;
  }
  return result;
}

/**
 * Convert a DOM `Range` inside `container` to a `Selector`.
 * Returns `null` if the range is collapsed or outside the container.
 */
export function captureSelectionFromRange(
  container: HTMLElement,
  range: Range,
): Selector | null {
  if (range.collapsed) return null;
  if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
    return null;
  }

  const nodes = walkTextNodes(container);
  const fullText = container.textContent ?? '';

  const findOffset = (targetNode: Node, targetOffset: number): number | null => {
    for (const { node, start } of nodes) {
      if (node === targetNode) return start + targetOffset;
    }
    return null;
  };

  const start = findOffset(range.startContainer, range.startOffset);
  const end = findOffset(range.endContainer, range.endOffset);
  if (start === null || end === null || start >= end) return null;

  return {
    position: { start, end },
    quote: {
      exact: fullText.slice(start, end),
      prefix: fullText.slice(Math.max(0, start - CONTEXT_LEN), start),
      suffix: fullText.slice(end, Math.min(fullText.length, end + CONTEXT_LEN)),
    },
  };
}

/**
 * Read the current browser `Selection`, validate it lies inside `container`,
 * and return a `Selector`. Returns `null` if nothing is selected.
 */
export function captureSelection(container: HTMLElement): Selector | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  return captureSelectionFromRange(container, sel.getRangeAt(0));
}
